import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, FileText, ArrowRight, ArrowLeft, Check, Lock } from "lucide-react";
import { API_URL } from "../config";
import VaultPicker, { recordVaultVerification } from "../secure/VaultPicker";

// NOTE: requires `npm i jszip` (used to unwrap Signed_Encrypted_*.zip packages).

const ACCEPTED_TYPES = ".pdf,.zip,.docx,.txt,.xlsx,.png,.jpg,.jpeg";

const steps = ["Upload Document", "Automatic Verification", "AI Temper Detection", "Policy & Key Validation", "Verification Result"];

const STEP_CIRCLE_CLASS = {
  done: "bg-green-500 border-green-500 text-white",
  current: "bg-blue-600 border-blue-600 text-white",
  upcoming: "bg-white border-gray-300 text-gray-500",
};

function getStepStatus(index, currentStep) {
  if (index < currentStep) return "done";
  if (index === currentStep) return "current";
  return "upcoming";
}

// Stable React key: a vault file id, else the object URL, else the name.
const docKey = (doc) => doc.vaultFileId || doc.url || doc.name;

async function postVerifyPdf(file, password) {
  const formData = new FormData();
  formData.append("pdf", file);

  if (password) {
    formData.append("encryption_password", password);
  }

  const response = await fetch(`${API_URL}/verify-pdf`, {
    method: "POST",
    body: formData,
  });
  return { response, result: await response.json() };
}

// File came from Secure Vault: save the outcome on that file
async function recordVaultOutcome(doc, result) {
  if (!doc.vaultFileId) return;
  await recordVaultVerification(doc.vaultFileId, result.document_valid ? "verified" : "failed");
}

// Carry the decryption password on the file entry so the AI Tamper
// Detection step can open the encrypted PDF too. It travels in router
// state only (in memory), never in the URL or localStorage.
function withPassword(docs, password) {
  return docs.map((d, i) => (i === 0 && password ? { ...d, encryptionPassword: password } : d));
}

function StepProgress({ currentStep }) {
  return (
    <div className="flex items-center justify-between mb-2 h-[72px]">
      {steps.map((step, index) => {
        const status = getStepStatus(index, currentStep);
        return (
          <div key={step} className="flex-1 flex flex-col items-center relative">
            {index !== steps.length - 1 && (
              <div className={`absolute top-5 left-1/2 w-full h-[2px] ${status === "done" ? "bg-green-500" : "bg-gray-300"}`} />
            )}
            <div className={`z-10 w-10 h-10 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${STEP_CIRCLE_CLASS[status]}`}>
              {status === "done" ? <Check size={12} /> : index + 1}
            </div>
            <p className="mt-1 text-sm font-medium text-center hidden sm:block text-black">{step}</p>
          </div>
        );
      })}
    </div>
  );
}

export default function SignDocument() {
  const navigate = useNavigate();
  const [currentStep] = useState(0);
  const [recentDocs, setRecentDocs] = useState([]);
  const fileInputRef = useRef(null);

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [pdfPassword, setPdfPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [showVault, setShowVault] = useState(false);
  const [dragging, setDragging] = useState(false);

  const toEntry = (file) => ({
    file, // IMPORTANT: the real File object is posted to the backend
    name: file.name,
    size: (file.size / 1024 / 1024).toFixed(2) + " MB",
    date: new Date().toLocaleDateString(),
    url: URL.createObjectURL(file),
  });

  // Accepts files from the picker or from drag & drop.
  // A signed package (Signed_Encrypted_*.zip / Signed_*.zip) is unwrapped and
  // the PDF inside is what gets verified.
  const addFiles = async (list) => {
    const entries = [];

    for (const f of Array.from(list || [])) {
      if (/\.zip$/i.test(f.name)) {
        try {
          const JSZip = (await import("jszip")).default;
          const zip = await JSZip.loadAsync(f);
          const pdfEntry = Object.values(zip.files).find(
            (z) => !z.dir && /\.pdf$/i.test(z.name)
          );
          if (!pdfEntry) {
            alert(`No PDF found inside ${f.name}`);
            continue;
          }
          const blob = await pdfEntry.async("blob");
          const pdfName = pdfEntry.name.split("/").pop();
          entries.push(toEntry(new File([blob], pdfName, { type: "application/pdf" })));
        } catch (err) {
          console.error(err);
          alert(`Could not read ${f.name}`);
        }
      } else {
        entries.push(toEntry(f));
      }
    }

    if (entries.length) {
      setRecentDocs((prev) => [...entries, ...prev]);
    }
  };

  const handleFileUpload = (event) => {
    addFiles(event.target.files);
    event.target.value = ""; // allows selecting the same file again
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  // File chosen from Secure Vault (already unlocked by the backend)
  const handleVaultSelect = (doc) => {
    setRecentDocs((prev) => [doc, ...prev.filter((d) => d.vaultFileId !== doc.vaultFileId)]);
  };

  const handleCancel = () => {
    setRecentDocs([]);
    alert("Signing process cancelled");
  };

  // OPEN FILE PICKER
  const handleBrowseClick = () => {
    fileInputRef.current.click();
  };

  // Wrong/missing password — (re)open the prompt instead of a dead-end alert
  const handleVerifyFailure = (result, password) => {
    if (result.requires_password) {
      setPasswordError(password ? (result.error || "Invalid password. Please try again.") : "");
      setPasswordModalOpen(true);
      return;
    }

    alert(result.error || "Verification failed");
  };

  // VERIFY DOCUMENT (optionally with a decryption password)
  const verifyDocument = async (password = "") => {
    if (recentDocs.length === 0) {
      alert("Please upload a document");
      return;
    }

    try {
      setVerifying(true);

      const { response, result } = await postVerifyPdf(recentDocs[0].file, password);

      console.log(result);

      if (!response.ok) {
        handleVerifyFailure(result, password);
        return;
      }

      setPasswordModalOpen(false);
      setPdfPassword("");
      setPasswordError("");

      await recordVaultOutcome(recentDocs[0], result);

      navigate("/automatic-verification", {
        state: {
          uploadedFiles: withPassword(recentDocs, password),
          verificationResult: result,
        },
      });
    } catch (error) {
      console.error(error);

      alert("Failed to verify document");
    } finally {
      setVerifying(false);
    }
  };

  const handleNext = () => verifyDocument();

  const handlePasswordSubmit = () => {
    if (!pdfPassword.trim()) {
      setPasswordError("Please enter the PDF password.");
      return;
    }
    verifyDocument(pdfPassword.trim());
  };

  const handlePasswordCancel = () => {
    setPasswordModalOpen(false);
    setPdfPassword("");
    setPasswordError("");
  };

  return (
    <div className="w-full bg-white mx-auto">
      <StepProgress currentStep={currentStep} />

      {/* UPLOAD BOX */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-2xl py-4 px-3 sm:py-6 sm:px-6 w-full max-w-2xl mx-auto text-center transition ${
          dragging
            ? "border-blue-600 bg-blue-50"
            : "border-blue-300 hover:border-blue-600 hover:bg-blue-50"
        }`}
      >
        <div className="flex justify-center mb-2">
          <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-full bg-blue-100 flex items-center justify-center shadow-md mb-2">
            <UploadCloud className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-blue-600 mb-2" />
          </div>
        </div>

        <p
          style={{
            fontSize: "16px",
            fontWeight: 500,
            color: "#374151",
          }}
        >
          Drag & Drop your document here
        </p>

        <p className="text-base font-normal text-gray-400 mt-2 mb-2">or</p>

        {/* HIDDEN FILE INPUT */}
        <input
          type="file"
          multiple
          accept={ACCEPTED_TYPES}
          ref={fileInputRef}
          className="hidden"
          onChange={handleFileUpload}
        />

        {/* BROWSE + VAULT BUTTONS */}
        <div className="flex flex-wrap items-center justify-center gap-3 mt-3">
          <button
            onClick={handleBrowseClick}
            className="inline-flex items-center justify-center h-[44px] px-6 bg-[#2563EB] text-white text-sm font-medium rounded-[10px] hover:bg-[#1D4ED8] transition"
          >
            Browse Files
          </button>
          <button
            onClick={() => setShowVault(true)}
            className="inline-flex items-center justify-center h-[44px] px-6 bg-white text-[#2563EB] border border-[#2563EB] text-sm font-medium rounded-[10px] hover:bg-blue-50 transition"
          >
            Select from Secure Vault
          </button>
        </div>

        <p className="text-sm font-normal text-gray-400 mt-4">
          Supported formats: PDF, ZIP (signed / encrypted package), DOCX, TXT, XLSX, PNG, JPG
          <br />
          Maximum file size: 100 MB
        </p>
      </div>

      {/* RECENT FILES */}
      <div className="sm:10 ">
        <div className="flex items-center justify-between ">
          <h3 className="text-base sm:text-14px md:text-xl font-semibold text-gray-700">
            Recent Documents
          </h3>

          <span className="text-sm text-gray-400">
            {recentDocs.length} Files
          </span>
        </div>

        {/* Empty */}
        {recentDocs.length === 0 && (
          <div className="border rounded-2xl  text-center text-gray-400 bg-gray-50">
            No files uploaded yet
          </div>
        )}

        {/* FILE LIST */}
        <div className="space-y-4">
          {recentDocs.map((doc) => (
            <div
              key={docKey(doc)}
              className="flex items-center justify-between border border-gray-200 rounded-xl px-3 py-2 hover:shadow-md hover:border-blue-300 transition"
            >
              <div className="flex items-center gap-4">
                <div className="bg-red-100 p-2 rounded-lg">
                  <FileText className="text-red-500 w-4 h-4" />
                </div>
                <div className="flex flex-col">
                  <p className="text-10px text-[#111827] break-all">
                    {doc.name}
                    {doc.vaultFileId && (
                      <span className="ml-2 px-2 py-0.5 text-[10px] rounded-full bg-green-100 text-green-700 font-medium">
                        Secure Vault
                      </span>
                    )}
                  </p>

                  <p className="text-xs text-[#4B5563]">
                    {doc.size} • {doc.date}
                  </p>
                </div>
              </div>

              {/* OPEN FILE */}
              <a
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1 bg-blue-50 text-blue-600 rounded-md hover:bg-blue-100 transition text-xs font-medium"
              >
                View
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* VIEW ALL */}
      <div
        onClick={() => {
          if (recentDocs.length === 0) { alert("No documents uploaded yet"); return; }
          const allDocs = recentDocs.map((doc, index) => `${index + 1}. ${doc.name} (${doc.size})`).join("\n");
          alert(`Uploaded Documents:\n\n${allDocs}`);
        }}
        className="text-blue-600 text-14px font-semibold mt-1 cursor-pointer hover:underline"
      >
        View all documents →
      </div>

      {/* FOOTER BUTTONS */}
      <div className="flex justify-between mt-3">
        <button onClick={handleCancel} className="inline-flex items-center gap-1.5 h-[44px] px-6 bg-gray-100 text-gray-600 rounded-[10px] hover:bg-gray-200 transition text-sm font-medium">
          <ArrowLeft size={14} /> Cancel
        </button>
        <button onClick={handleNext} disabled={verifying} className="flex items-center gap-1.5 h-[44px] px-6 py-4 rounded-[10px] text-white text-sm font-medium bg-[#2563EB] hover:bg-[#1D4ED8] transition disabled:opacity-60 disabled:cursor-not-allowed">
          {verifying ? "Verifying..." : "Next"} <ArrowRight size={14} />
        </button>
      </div>

      {/* SECURE VAULT PICKER */}
      <VaultPicker
        open={showVault}
        purpose="verify"
        onClose={() => setShowVault(false)}
        onSelect={handleVaultSelect}
      />

      {/* ENCRYPTION PASSWORD MODAL */}
      {passwordModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={handlePasswordCancel}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Lock className="text-blue-600" size={20} />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                Document is encrypted
              </h3>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              This PDF is password-protected. Enter the encryption password to continue verification.
            </p>

            <input
              type="password"
              autoFocus
              value={pdfPassword}
              onChange={(e) => {
                setPdfPassword(e.target.value);
                if (passwordError) setPasswordError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handlePasswordSubmit();
              }}
              placeholder="Enter PDF password"
              className={`w-full h-[44px] px-3 rounded-[10px] border text-sm outline-none ${
                passwordError ? "border-red-400 focus:border-red-500" : "border-gray-300 focus:border-blue-500"
              }`}
            />

            {passwordError && (
              <p className="text-red-500 text-xs mt-2">{passwordError}</p>
            )}

            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={handlePasswordCancel}
                className="h-[40px] px-5 bg-gray-100 text-gray-600 rounded-[10px] hover:bg-gray-200 transition text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordSubmit}
                disabled={verifying}
                className="h-[40px] px-5 bg-[#2563EB] text-white rounded-[10px] hover:bg-[#1D4ED8] transition text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {verifying ? "Checking..." : "Verify"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}